import { useObjectContext } from '@context/ItemZoneContext'
import { useAppStore } from '@store'
import * as Accordion from '@radix-ui/react-accordion'
import { TexturesGroup, BackMaterialInfo } from '@types'
import { getTextureImage } from '@utils/sceneObjects/getTextureImage'
import clsx from 'clsx'
import { useState, useEffect, useRef } from 'react'
import { API } from '@utils/queryBuilder'
import { SCROLL_OFFSET } from '@constants'
import { localizeFabricDisplayName } from '@utils/budget/fabricCharacteristics'
import { ui } from '@utils/i18n/uiCopy'
import arrowRight from '@assets/nuevos-iconos/arrow-circle-right.png'
import arrowDown from '@assets/nuevos-iconos/arrow-circle-down.png'

const paddingDictionary = {
  0: 'pl-0',
  1: 'pl-[1.25rem]',
  2: 'pl-[2.5rem]',
  3: 'pl-[3.75rem]',
  4: 'pl-[5rem]',
} as const

// Nombres de grupos de texturas que NO deben mostrarse para areas y walls
const EXCLUDED_GROUP_NAMES = [
  'Alfombras',
  'Cristales',
  'Cuadros',
  'Encimeras',
  'Maderas',
  'Marmol',
  'Varios',
]

type Props = {
  groups?: TexturesGroup[]
  nestingLevel: number
  currentTexture: any
  onTextureClick: (texture: BackMaterialInfo) => void
  selectedTextureId?: number
  /** Cuando viene definido (p. ej. modo universal sin item), el nivel 0 usa este valor en lugar de controlledValues */
  rootAccordionValue?: string
}

export default function TexturesGroupWithTextures({
  groups,
  nestingLevel,
  currentTexture,
  onTextureClick,
  selectedTextureId,
  rootAccordionValue,
}: Props) {
  const { controlledValues, setControlledValues, centerInfo } = useObjectContext()

  const currentObjectID = useAppStore((s) => s.currentObjectID)
  const selected = useAppStore((s) => s.items.get(currentObjectID) ?? s.areas.get(currentObjectID))
  const isAreaOrWall = selected?.type === 'areas' || selected?.type === 'walls'

  const toRender = groups ?? centerInfo
  if (!toRender || !Array.isArray(toRender)) return null

  // Filtrar duplicados por nombre (normalizado) y grupos no relevantes
  const seenNames = new Set<string>()
  const seenIds = new Set<number>()

  // Primero, identificar duplicados antes de filtrar
  const groupsWithNormalizedNames = toRender
    .map((group: TexturesGroup) => {
      if (!group || !group.idGruposDeTexturas) return null
      const normalizedName = group.nombre?.toLowerCase().trim().replace(/\s+/g, '')
      const normalizedNameSingular = normalizedName.endsWith('s')
        ? normalizedName.slice(0, -1)
        : normalizedName
      const normalizedNamePlural = normalizedName + 's'
      return {
        group,
        normalizedName,
        normalizedNameSingular,
        normalizedNamePlural,
      }
    })
    .filter(Boolean)

  // Identificar cuál de los duplicados mantener (preferir el singular o el primero)
  const duplicateMap = new Map<string, TexturesGroup[]>()
  groupsWithNormalizedNames.forEach(
    ({ group, normalizedName, normalizedNameSingular, normalizedNamePlural }) => {
      const key = normalizedNameSingular // Usar el singular como clave
      if (!duplicateMap.has(key)) {
        duplicateMap.set(key, [])
      }
      duplicateMap.get(key)!.push(group)
    },
  )

  // Para cada grupo de duplicados, mantener el que tiene subgrupos o más información
  const groupsToKeep = new Set<number>()
  duplicateMap.forEach((groups, key) => {
    if (groups.length > 1) {
      // Preferir el que tiene subgrupos (más información)
      const withSubGroups = groups.find((g) => g.subGrupos && g.subGrupos.length > 0)
      if (withSubGroups) {
        groupsToKeep.add(withSubGroups.idGruposDeTexturas)
      } else {
        // Si ninguno tiene subgrupos, mantener el plural (generalmente más completo)
        const plural = groups.find((g) => g.nombre?.toLowerCase().endsWith('s'))
        const toKeep = plural || groups[0]
        groupsToKeep.add(toKeep.idGruposDeTexturas)
      }
    } else {
      groupsToKeep.add(groups[0].idGruposDeTexturas)
    }
  })

  const filteredGroups = toRender.filter((group: TexturesGroup) => {
    if (!group || !group.idGruposDeTexturas) {
      return false
    }

    // Evitar duplicados por ID
    if (seenIds.has(group.idGruposDeTexturas)) {
      return false
    }
    seenIds.add(group.idGruposDeTexturas)

    // Verificar si este grupo fue marcado como duplicado y debe eliminarse
    if (!groupsToKeep.has(group.idGruposDeTexturas)) {
      return false
    }

    // Filtrar grupos excluidos para areas y walls
    if (isAreaOrWall && nestingLevel === 0 && EXCLUDED_GROUP_NAMES.includes(group.nombre)) {
      return false
    }

    // Filtrar grupos no relevantes para items que no son areas/walls
    // NOTA: El filtrado debe ser conservador - solo eliminar grupos claramente no relevantes
    // El backend debería devolver grupos relevantes, pero algunos pueden ser genéricos
    // if (!isAreaOrWall && nestingLevel === 0) {
    //   // Solo filtrar grupos claramente de construcción/paredes/suelos que NUNCA se usan en muebles
    //   const constructionOnlyGroups = [
    //     'Cemento',
    //     'Ladrillo',
    //     'Marazzi',
    //     'Porcelanico',
    //     'Gres',
    //     'Porcelanosa',
    //     'Parquet',
    //     'Papel Pintado',
    //   ]

    //   const matchesConstruction = constructionOnlyGroups.some(
    //     (cog) => group.nombre?.toLowerCase() === cog.toLowerCase(),
    //   )

    //   if (matchesConstruction) {
    //     return false
    //   }

    //   // NO filtrar otros grupos como "Encimeras", "Cuadros", "Cristales", "Marmol", "Varios"
    //   // porque pueden tener texturas relevantes para diferentes tipos de items
    // }

    return true
  })

  return (
    <>
      {filteredGroups.map((group: TexturesGroup) => {
        const hasSubGroups = group.subGrupos && group.subGrupos.length > 0
        const groupID = group.idGruposDeTexturas
        const isExpanded =
          nestingLevel === 0 && rootAccordionValue !== undefined
            ? rootAccordionValue === String(groupID)
            : controlledValues[nestingLevel] === groupID

        return (
          <Accordion.Item
            value={String(groupID)}
            key={groupID}
          >
            <Accordion.Header className={clsx('relative', 'transition-all duration-200')}>
              <Accordion.Trigger
                className={clsx(
                  'relative w-full border-0 px-4 py-2 text-left text-sm transition-all duration-200',
                  'cursor-pointer hover:bg-C/Color4',
                  isExpanded
                    ? 'bg-C/Color2 font-semibold underline decoration-2 underline-offset-2'
                    : 'bg-white',
                )}
              >
                <div className='flex items-center'>
                  <span
                    className={clsx(
                      paddingDictionary[nestingLevel as keyof typeof paddingDictionary] ?? 'pl-0',
                      'pl-2',
                      nestingLevel === 0 ? 'ml-6' : 'ml-8',
                    )}
                  >
                    {localizeFabricDisplayName(group.nombre) || group.nombre}
                  </span>
                  {hasSubGroups && (
                    <img
                      src={isExpanded ? arrowDown : arrowRight}
                      width='20'
                      height='20'
                      alt={isExpanded ? ui('groupOpen') : ui('groupClosed')}
                      className='ml-2 transition-opacity duration-200'
                    />
                  )}
                </div>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className='AccordionContent overflow-hidden'>
              {hasSubGroups ? (
                <Accordion.Root
                  type='single'
                  collapsible
                  value={
                    controlledValues[nestingLevel + 1]
                      ? String(controlledValues[nestingLevel + 1])
                      : ''
                  }
                  onValueChange={(value) => {
                    const newValues = controlledValues.slice(0, nestingLevel + 1)
                    newValues[nestingLevel + 1] = value ? Number(value) : 0
                    setControlledValues(newValues)
                  }}
                >
                  <TexturesGroupWithTextures
                    groups={group.subGrupos}
                    nestingLevel={nestingLevel + 1}
                    currentTexture={currentTexture}
                    onTextureClick={onTextureClick}
                    selectedTextureId={selectedTextureId}
                    rootAccordionValue={rootAccordionValue}
                  />
                </Accordion.Root>
              ) : (
                <GroupTextures
                  groupID={groupID}
                  groupName={group.nombre}
                  onTextureClick={onTextureClick}
                  selectedTextureId={selectedTextureId}
                  isExpanded={isExpanded}
                />
              )}
            </Accordion.Content>
          </Accordion.Item>
        )
      })}
    </>
  )
}

function GroupTextures({
  groupID,
  groupName,
  onTextureClick,
  selectedTextureId,
  isExpanded,
}: {
  groupID: number
  groupName: string
  onTextureClick: (texture: BackMaterialInfo) => void
  selectedTextureId?: number
  isExpanded: boolean
}) {
  const [textures, setTextures] = useState<BackMaterialInfo[]>([])
  const [loading, setLoading] = useState(true)
  const scrollableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isExpanded) {
      setTextures([])
      setLoading(false)
      return
    }

    let cancelled = false

    async function fetchTextures() {
      setLoading(true)
      try {
        const response = await API().api.texturasGetTexturas({ idGruposDeTexturas: groupID })
        if (!cancelled && response?.data) {
          setTextures(response.data)
        }
      } catch (error) {
        console.error('Error fetching textures:', error)
        if (!cancelled) {
          setTextures([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchTextures()

    return () => {
      cancelled = true
    }
  }, [groupID, isExpanded])

  // Hacer scroll cuando cambia la textura seleccionada, el grupo, o se cargan las texturas
  useEffect(() => {
    if (!isExpanded || !selectedTextureId || textures.length === 0 || loading) {
      return
    }

    // Esperar a que el DOM se actualice completamente después de cargar las texturas
    // Usar múltiples requestAnimationFrame para asegurar que el DOM esté completamente renderizado
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!scrollableRef.current) {
            return
          }

          // Buscar por clase .elElegido primero (como hace useScrollOnLoad)
          let selectedElement = scrollableRef.current.querySelector<HTMLDivElement>('.elElegido')

          // Si no se encuentra, buscar por el ID de la textura
          if (!selectedElement) {
            selectedElement = scrollableRef.current.querySelector<HTMLDivElement>(
              `.texture-${selectedTextureId}`,
            )
          }

          if (selectedElement && scrollableRef.current) {
            const container = scrollableRef.current
            const elementTop = selectedElement.offsetTop
            const containerOffsetTop = container.offsetTop || 0
            const desiredScrollTop = elementTop - containerOffsetTop - SCROLL_OFFSET

            container.scrollTo({
              top: Math.max(0, desiredScrollTop),
              behavior: 'smooth',
            })
          }
        })
      })
    }, 500) // Aumentar el delay para asegurar que el DOM esté completamente renderizado

    return () => clearTimeout(timer)
  }, [selectedTextureId, isExpanded, textures.length, loading, groupID]) // Agregar groupID como dependencia

  if (loading) {
    return <div className='p-4 text-center text-sm text-C/Color3'>Cargando texturas...</div>
  }

  if (textures.length === 0) {
    return <div className='p-4 text-center text-sm text-C/Color3'>No hay texturas disponibles</div>
  }

  return (
    <div className='p-3'>
      <div
        ref={scrollableRef}
        className='textures-grid-scroll grid max-h-[500px] grid-cols-3 gap-2 overflow-y-auto px-1 pr-3'
        style={{ scrollbarGutter: 'stable' }}
      >
        {textures.map((texture: BackMaterialInfo) => {
          const isSelected = selectedTextureId === texture.idTexturas
          return (
            <div
              key={`${texture.idTexturas}`}
              className={clsx(
                'relative cursor-pointer p-0.5',
                `texture-${texture.idTexturas}`,
                isSelected && 'selected elElegido',
              )}
              onClick={(e) => {
                e.stopPropagation()
                onTextureClick(texture)
              }}
            >
              <img
                role='button'
                loading='lazy'
                src={getTextureImage(texture)}
                className={clsx(
                  'h-20 w-full cursor-pointer rounded object-contain',
                  isSelected && 'ring-2 ring-colorAtemporal ring-offset-2',
                )}
                style={{ imageRendering: 'auto' }}
                alt={texture.nombre}
              />
              {/* Nombre de la textura en la esquina inferior derecha */}
              <div
                className={clsx(
                  'absolute bottom-0 right-0 rounded-tl bg-black bg-opacity-70 px-1 py-0.5 text-xs text-white',
                  isSelected && 'font-extrabold',
                )}
                style={{
                  maxWidth: '70px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={texture.nombre}
              >
                {texture.nombre}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
